import React, { useCallback } from 'react';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { useResolve } from '../../Container';
import { UIField, UIFieldProps } from '../../canvas/entities/UIField';
import { PropertyStripLayout } from '../../canvas/properties/PropertyStripLayout';
import { textAlignHSelector, textAlignVSelector } from '../../canvas/properties/TextPropertyStripParts';
import { useComponentObserver } from '../../canvas/visualization/ComponentObserver';
import { AtsertAnnotationWidget, useAtsertWidgetBridge } from '../annotations/AnnotationAtsertWidget';
import { submitMentions } from '../annotations/MentionCommands';
import { QuillInput } from '../mentions/QuillInput';
import { DisabledColorWidget } from './DisabledColorWidget';
import { Sticky } from './Sticky';

const propertyLayout = PropertyStripLayout<Sticky>((d, s, o, w, a) => {
    const hasAutoColor = !!a.get('autoColor', { ifVarying: 'true' });
    return [
        // Background
        hasAutoColor ? (
            w.createInline({
                ui: DisabledColorWidget
            })
        ) : (
            w.createColorSelector({
                value: a.get('background'),
                text: 'Sticky Color',
                onSelect() {
                    o.update(u => {
                        u.rolling(a.keyFor('background'));
                        a.set('background', this.value!);
                    });
                }
            })
        ),
        ...textAlignHSelector('contentTextAlignH')(d, s, o, w, a),
        ...textAlignVSelector('contentTextAlignV')(d, s, o, w, a),
    ];
});

export const StickyContentFieldEditor = ({ active, field }: UIFieldProps) => {

    const objects = useResolve(ObjectManager);
    const dispatch = useResolve(CommandDispatcher);
    const sticky = field.owner.cast(Sticky);
    useComponentObserver(sticky);

    const onChange = useCallback((nextValue: string) => {
        objects.update(u => {
            u.rolling(UIField.key(field));
            sticky.content = nextValue;
        });
    }, [field]);

    const onCommit = useCallback(() => {
        objects.update(u => u.noop());
        dispatch(submitMentions, [[sticky.id]]);
    }, []);

    const atsert = useAtsertWidgetBridge({ name: 'atsertAnnotation', ui: AtsertAnnotationWidget }, sticky);

    return <QuillInput key={UIField.key(field)} {...{
        initialValue: sticky.content,
        onChange,
        onCommit,
        atsert,
        formatting: ['mention'],
        textSettings: sticky.layout.content.settings,
        maxLength: sticky.width > 150 ? 320 : 180,
    }} />;
};

export const StickyContentField = (owner: Sticky): UIField => ({
    ui: StickyContentFieldEditor,
    name: 'content',
    bounds: owner.layout.content.bounds,
    owner,
    propertyLayout,
});
